import {useCallback, useMemo, useRef, useState} from "react";
import {BeatPlanningCalendar, type BeatPlanningCalendarEvent} from "~/components/beatPlanningCalendar/beatPlanningCalendar";
import {
    getDefaultCalendarViewModeForViewport,
    readIsMobileCalendarViewportSync,
    type CalendarViewMode,
} from "~/components/beatPlanningCalendar/calendarToolbarBus";
import type {OttopilotDataViewerController} from "~/components/ottopilotDataViewer/ottopilotDataViewerController";
import {RowEngagementPanelType, RowEngagementType} from "~/components/ottopilotDataViewer/rowEngagementPanel";
import type {OttopilotView, ViewType} from "~/components/ottopilotDataViewer/view";
import {useCalendarRows} from "~/components/ottopilotDataViewer/viewDataHooks";
import {CALENDAR_DAY_MODAL_PAGE_SIZE} from "~/components/ottopilotDataViewer/calendarFetchLimits";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";

type RowWithCalendarFields = {
    id: Uuid;
    title?: string;
    startDate?: string;
    endDate?: string;
    isAlldayEvent?: boolean;
};

function isValidDateInput(value: string | undefined): value is string {
    if (value === undefined || value.trim() === "") {
        return false;
    }
    return Number.isNaN(Date.parse(value)) === false;
}

function mapRowToCalendarEvent<T extends {id: Uuid}>(
    row: T & RowWithCalendarFields,
    resolveStartDate: (row: T) => string | undefined,
    eventType?: string,
    templateLabel?: string,
    templateStrokeColor?: string,
): BeatPlanningCalendarEvent | null {
    const start = resolveStartDate(row);
    if (isValidDateInput(start) === false) {
        return null;
    }
    const end = isValidDateInput(row.endDate) ? row.endDate : start;

    return {
        id: row.id,
        title: row.title ?? "",
        start: start,
        end: end,
        allDay: row.isAlldayEvent === true,
        type: eventType,
        templateLabel: templateLabel,
        extendedProps: {
            rawRow: row,
            templateLabel: templateLabel,
            templateStrokeColor: templateStrokeColor,
        },
    };
}

export function CalendarView<
    T extends {
        id: Uuid;
    },
>({
    controller,
    view,
    className,
}: {
    controller: OttopilotDataViewerController<T>;
    view: OttopilotView<T> & {
        viewType: ViewType.calendar;
    };
    className?: string;
}) {
    const [visibleRange, setVisibleRange] = useState<{start: Date; end: Date} | null>(null);
    const [calendarViewMode, setCalendarViewMode] = useState<CalendarViewMode>(() =>
        getDefaultCalendarViewModeForViewport(readIsMobileCalendarViewportSync()),
    );
    const calendarViewModeRef = useRef(calendarViewMode);
    calendarViewModeRef.current = calendarViewMode;
    const calendarRowsQuery = useCalendarRows({
        controller: controller,
        visibleRangeStart: visibleRange?.start ?? null,
        visibleRangeEnd: visibleRange?.end ?? null,
        calendarViewMode: calendarViewMode,
    });

    const mapRow = useCallback(
        (row: T) => {
            const getEventType = view.getEventType;
            const getEventTemplateLabel = view.getEventTemplateLabel;
            const getEventTemplateStrokeColor = view.getEventTemplateStrokeColor;
            return mapRowToCalendarEvent(
                row as T & RowWithCalendarFields,
                controller.resolveCalendarRowStartDate,
                getEventType?.(row),
                getEventTemplateLabel?.(row),
                getEventTemplateStrokeColor?.(row),
            );
        },
        [
            controller.resolveCalendarRowStartDate,
            view.getEventType,
            view.getEventTemplateLabel,
            view.getEventTemplateStrokeColor,
        ],
    );

    const mappedEvents = useMemo<Array<BeatPlanningCalendarEvent>>(() => {
        if (calendarRowsQuery.isYearCountsView === true) {
            return [];
        }
        const rows = calendarRowsQuery.data;
        if (rows === undefined) {
            return [];
        }
        return rows.reduce<Array<BeatPlanningCalendarEvent>>((acc, row) => {
            const mapped = mapRow(row);
            if (mapped !== null) {
                acc.push(mapped);
            }
            return acc;
        }, []);
    }, [calendarRowsQuery.data, calendarRowsQuery.isYearCountsView, mapRow]);

    const hasCalendarRows =
        calendarRowsQuery.isYearCountsView === true
            ? calendarRowsQuery.yearStats !== undefined
            : calendarRowsQuery.data !== undefined;
    // Disabled queries are `isPending` in TanStack Query v5. Only overlay while a
    // fetch is actually in flight, otherwise a wiped range freezes the calendar.
    const isScheduleDataLoading = calendarRowsQuery.isFetching === true && hasCalendarRows === false;
    const isBackgroundFetching = calendarRowsQuery.isFetching === true && hasCalendarRows === true;
    const initialError = hasCalendarRows === false ? calendarRowsQuery.error : null;
    const scheduleLoadMoreError = hasCalendarRows === true ? calendarRowsQuery.error : null;
    const unavailableMessage = (() => {
        if (calendarRowsQuery.unavailableReason === null || calendarRowsQuery.unavailableReason === "allowed") {
            return null;
        }
        switch (calendarRowsQuery.unavailableReason) {
            case "unsupported": return "Year view is not supported for this calendar.";
            case "loading": return "Checking calendar access…";
            case "denied": return "You do not have permission to view this calendar.";
            case "absent":
            case "disabled": return "This calendar view is not available for this workspace.";
        }
    })();
    const dayModalRequestContextKey = [
        ...controller.queryKeyPrefix,
        controller.filterConfiguration.revisionId,
        controller.searchConfiguration.revisionId,
        controller.sortConfiguration.revisionId,
        controller.calendarDisplayMetricId,
        controller.activeCalendarDateColumnId,
        controller.calendarDataSource,
        controller.calendarOrchestrationEligibility,
    ].join(":");

    const fetchDayEventsForModal = useCallback(
        async (date: Date, offset: number) => {
            const rows = await calendarRowsQuery.fetchDayEvents({
                date: date,
                limit: CALENDAR_DAY_MODAL_PAGE_SIZE,
                offset: offset,
            });
            return rows
                .map((row) => mapRow(row))
                .filter((event): event is BeatPlanningCalendarEvent => event !== null)
                .map((event) => {
                    const start = new Date(event.start);
                    const end = new Date(event.end);
                    const color = view.getEventColor?.(event.type) ?? {};
                    const typeLabel =
                        event.type === undefined ? undefined : view.getEventTypeLabel?.(event.type);
                    return {
                        id: event.id,
                        title: event.title,
                        start: Number.isNaN(start.getTime()) ? null : start,
                        end: Number.isNaN(end.getTime()) ? null : end,
                        allDay: event.allDay === true,
                        backgroundColor: color.backgroundColor,
                        borderColor: color.borderColor,
                        textColor: color.textColor,
                        extendedProps: {
                            ...event.extendedProps,
                            type: event.type,
                            typeLabel: typeLabel,
                            templateLabel: event.templateLabel ?? event.extendedProps?.templateLabel,
                            templateStrokeColor:
                                event.templateStrokeColor ?? event.extendedProps?.templateStrokeColor,
                        },
                    };
                });
        },
        [calendarRowsQuery, mapRow, view.getEventColor, view.getEventTypeLabel],
    );

    const handleVisibleRangeChange = useCallback((range: {start: Date; end: Date}) => {
        setVisibleRange({
            start: range.start,
            end: range.end,
        });
    }, []);

    const handleCalendarViewModeChange = useCallback((mode: CalendarViewMode) => {
        if (calendarViewModeRef.current === mode) {
            return;
        }
        calendarViewModeRef.current = mode;
        setVisibleRange(null);
        setCalendarViewMode(mode);
    }, []);

    return (
        <div className={className ?? "w-full h-full relative"}>
            <BeatPlanningCalendar<T>
                events={mappedEvents}
                externalYearStats={calendarRowsQuery.yearStats}
                fetchDayEventsForModal={
                    view.eventOpensDaySummaryModal === true ? fetchDayEventsForModal : undefined
                }
                getEventColor={view.getEventColor}
                getEventTypeLabel={view.getEventTypeLabel}
                hasActiveFilters={
                    controller.filterConfiguration.lineItems.length > 0 ||
                    controller.searchConfiguration.searchValue.trim() !== ""
                }
                isScheduleDataLoading={isScheduleDataLoading}
                scheduleHasMore={calendarRowsQuery.hasNextPage}
                isScheduleLoadingMore={calendarRowsQuery.isFetchingNextPage}
                scheduleLoadMoreError={scheduleLoadMoreError?.message ?? null}
                onScheduleLoadMore={calendarRowsQuery.hasNextPage === true || scheduleLoadMoreError !== null
                    ? () => { void calendarRowsQuery.fetchNextPage(); }
                    : undefined}
                onCreateEventClick={view.onCreateEventClick}
                scheduleEmptyState={view.scheduleEmptyState}
                legendItems={view.legendItems}
                allDayStyle={view.allDayStyle}
                startTimeOnly={view.startTimeOnly}
                use24HourTimeAxis={view.use24HourTimeAxis === true}
                disableCreateEventWhenViewYearAfterCurrent={view.disableCreateEventWhenViewYearAfterCurrent === true}
                disableCreateEventButton={view.disableCreateEventButton === true}
                eventOpensDaySummaryModal={view.eventOpensDaySummaryModal === true}
                daySummaryModalPresentation={view.calendarDaySummaryModalPresentation}
                onVisibleRangeChange={handleVisibleRangeChange}
                onCalendarViewModeChange={handleCalendarViewModeChange}
                dayModalRequestContextKey={dayModalRequestContextKey}
                onEventClick={(_event, rawRow) => {
                    if (rawRow === undefined || controller.readPanelComponent === undefined) {
                        return;
                    }
                    const openReadPanel = () => {
                        controller.setCurrentlyOpenRowEngagementPanel({
                            rowEngagementType: RowEngagementType.read,
                            rowEngagementPanelType: RowEngagementPanelType.sidebar,
                            rowEngagementData: rawRow,
                        });
                    };
                    if (view.onCalendarEventClick !== undefined) {
                        view.onCalendarEventClick({row: rawRow, openReadPanel});
                        return;
                    }
                    openReadPanel();
                }}
                className="w-full h-full"
            />
            {isScheduleDataLoading === true ? (
                <div
                    className="absolute inset-0 flex items-center justify-center z-10"
                    style={{
                        backgroundColor: "color-mix(in srgb, var(--ds-surface, #ffffff) 70%, transparent)",
                    }}
                >
                    <div
                        className="rounded-full px-4 py-2 text-sm"
                        style={{
                            border: "1px solid var(--ds-border, #dfe1e6)",
                            backgroundColor: "var(--ds-surface-overlay, #ffffff)",
                            color: "var(--ds-text, #172b4d)",
                        }}
                    >
                        Loading calendar data...
                    </div>
                </div>
            ) : null}
            {unavailableMessage !== null && isScheduleDataLoading === false ? (
                <div role="status" className="absolute inset-0 flex items-center justify-center z-10">
                    <div className="rounded px-4 py-3 text-sm bg-white border">{unavailableMessage}</div>
                </div>
            ) : null}
            {initialError !== null && unavailableMessage === null ? (
                <div role="alert" className="absolute inset-0 flex items-center justify-center z-10">
                    <div className="rounded px-4 py-3 text-sm bg-white border text-center">
                        <div>{initialError.message || "Unable to load calendar data."}</div>
                        <button type="button" className="mt-2 underline" onClick={() => { void calendarRowsQuery.retry(); }}>
                            Retry
                        </button>
                    </div>
                </div>
            ) : null}
            {isBackgroundFetching === true ? (
                <div className="absolute top-2 right-2 z-10 pointer-events-none">
                    <div
                        className="rounded-full px-3 py-1 text-xs"
                        style={{
                            border: "1px solid var(--ds-border, #dfe1e6)",
                            backgroundColor: "var(--ds-surface-overlay, #ffffff)",
                            color: "var(--ds-text-subtle, #44546f)",
                        }}
                    >
                        Updating...
                    </div>
                </div>
            ) : null}
        </div>
    );
}
